import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NotificationsIcon from "@mui/icons-material/NotificationsActive";
import BlockIcon from "@mui/icons-material/Block";
import HandshakeIcon from "@mui/icons-material/Handshake";
import TimerIcon from "@mui/icons-material/Timer";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { authState } from "../../Recoil/RecoilState";
import { fetchLostFoundCameras, fetchLostFoundSummary } from "../../services/lostFoundApi";
import HourHeatmap from "./HourHeatmap";
import { errorText, formatDuration, formatNumber, titleCase } from "./lostFoundConstants";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, ChartTooltip, Legend);

const WINDOWS = [
  { value: 1, label: "Last 24 hours" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

// The busiest cameras are the story; the long tail stays in the table below.
const CAMERA_CHART_LIMIT = 10;

// Buckets are cut in the viewer's zone, so "busiest hour" means their hour.
const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const formatPct = (value) => (value === null || value === undefined ? "—" : `${Number(value).toFixed(1)}%`);
const share = (part, whole) => (whole ? (100 * part) / whole : null);

const StatCard = ({ label, value, hint, icon, color }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h5" fontWeight="bold">{value}</Typography>
          {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
        </Box>
        <Box sx={{ color: `${color}.main` }}>{icon}</Box>
      </Stack>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, subtitle, children }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      <Box sx={{ mt: 2 }}>{children}</Box>
    </CardContent>
  </Card>
);

/** Detection to reunion, one bar per stage, scaled to the largest stage. */
const Funnel = ({ stages }) => {
  const max = Math.max(1, ...stages.map((stage) => stage.value));
  return (
    <Stack spacing={2.25}>
      {stages.map((stage) => (
        <Box key={stage.key}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
            <Typography variant="body2">{stage.label}</Typography>
            <Typography variant="body2" fontWeight={600}>{formatNumber(stage.value)}</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={(100 * stage.value) / max} sx={{ height: 8, borderRadius: 4, my: 0.5 }} />
          {stage.caption && <Typography variant="caption" color="text.secondary">{stage.caption}</Typography>}
        </Box>
      ))}
    </Stack>
  );
};

/** What the cameras flagged, when, where, and how much of it got back to its owner. */
const LostFoundAnalytics = () => {
  const theme = useTheme();
  const { token } = useRecoilValue(authState);
  const [days, setDays] = useState(7);
  const [cameraId, setCameraId] = useState("");
  const [cameras, setCameras] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLostFoundCameras(token, false)
      .then((list) => setCameras(Array.isArray(list) ? list : []))
      .catch(() => setCameras([]));
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await fetchLostFoundSummary({ days, camera_id: cameraId || undefined, tz: TIMEZONE }, token));
    } catch (err) {
      setError(errorText(err, "Could not load analytics."));
    } finally {
      setLoading(false);
    }
  }, [days, cameraId, token]);

  useEffect(() => {
    if (token) load();
  }, [load, token]);

  const kpis = summary?.kpis;
  const hourly = summary?.window?.bucket === "hour";
  const hasEvents = Boolean(kpis?.events);
  // A false positive is noise the operator dismissed: muted grey, not an alarm colour.
  const dismissed = theme.palette.grey[500];

  const baseOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      scales: {
        x: {
          ticks: { color: theme.palette.text.secondary, maxRotation: 0, autoSkipPadding: 12 },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { color: theme.palette.text.secondary, precision: 0 },
          grid: { color: theme.palette.divider },
        },
      },
      plugins: {
        legend: { position: "top", align: "end", labels: { color: theme.palette.text.secondary, boxWidth: 10, boxHeight: 10 } },
        datalabels: { display: false },
      },
    }),
    [theme]
  );

  const trend = useMemo(() => {
    const points = summary?.series || [];
    return {
      labels: points.map((point) => dayjs(point.bucket_start).format(hourly ? "HH:mm" : "MMM D")),
      datasets: [
        {
          label: "Alerts",
          data: points.map((point) => point.events),
          borderColor: theme.palette.primary.main,
          backgroundColor: `${theme.palette.primary.main}22`,
          fill: true,
          borderWidth: 2,
          cubicInterpolationMode: "monotone",
          pointRadius: 0,
          pointHoverRadius: 5,
        },
        {
          label: "False positives",
          data: points.map((point) => point.false_positives),
          borderColor: dismissed,
          backgroundColor: dismissed,
          borderWidth: 2,
          cubicInterpolationMode: "monotone",
          pointRadius: 0,
          pointHoverRadius: 5,
        },
      ],
    };
  }, [summary, hourly, theme, dismissed]);

  const byCamera = useMemo(() => {
    const rows = (summary?.by_camera || []).slice(0, CAMERA_CHART_LIMIT);
    return {
      height: Math.max(160, rows.length * 30 + 48),
      data: {
        labels: rows.map((row) => row.camera_name || "Deleted camera"),
        datasets: [
          { label: "Alerts", data: rows.map((row) => row.events - row.false_positives),
            backgroundColor: theme.palette.primary.main, borderRadius: 4, barThickness: 16 },
          { label: "False positives", data: rows.map((row) => row.false_positives),
            backgroundColor: dismissed, borderRadius: 4, barThickness: 16 },
        ],
      },
      options: {
        ...baseOptions,
        indexAxis: "y",
        interaction: { mode: "index", axis: "y", intersect: false },
        scales: { x: { ...baseOptions.scales.y, stacked: true }, y: { ...baseOptions.scales.x, stacked: true } },
      },
    };
  }, [summary, theme, baseOptions, dismissed]);

  const byClass = useMemo(() => {
    const rows = summary?.by_class || [];
    return {
      height: Math.max(160, rows.length * 30 + 24),
      data: {
        labels: rows.map((row) => titleCase(row.class_name)),
        datasets: [{ label: "Alerts", data: rows.map((row) => row.events),
          backgroundColor: theme.palette.primary.main, borderRadius: 4, barThickness: 16 }],
      },
      options: {
        ...baseOptions,
        indexAxis: "y",
        interaction: { mode: "index", axis: "y", intersect: false },
        scales: { x: baseOptions.scales.y, y: baseOptions.scales.x },
        plugins: { ...baseOptions.plugins, legend: { display: false } },
      },
    };
  }, [summary, theme, baseOptions]);

  const funnel = summary?.funnel;
  const funnelStages = funnel
    ? [
        { key: "detected", label: "Alerts raised", value: funnel.events_detected },
        {
          key: "harvested", label: "Added to found gallery", value: funnel.items_harvested,
          caption: funnel.events_detected
            ? `${formatPct(share(funnel.items_harvested, funnel.events_detected))} of alerts` : null,
        },
        {
          key: "reports", label: "Lost reports filed", value: funnel.reports_filed,
          caption: cameraId ? "All cameras — a report is not tied to one" : null,
        },
        {
          key: "reunions", label: "Reunited with owner", value: funnel.reunions,
          caption: funnel.reports_filed ? `${formatPct(share(funnel.reunions, funnel.reports_filed))} of reports` : null,
        },
      ]
    : [];

  const windowLabel = WINDOWS.find((option) => option.value === days)?.label.toLowerCase();

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
          Times in {TIMEZONE}.
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField select size="small" label="Camera" value={cameraId} onChange={(e) => setCameraId(e.target.value)}
            sx={{ minWidth: 200 }}>
            <MenuItem value="">All cameras</MenuItem>
            {cameras.map((cam) => (
              <MenuItem key={cam.stream_id} value={cam.stream_id}>{cam.camera_name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Window" value={days} onChange={(e) => setDays(Number(e.target.value))}
            sx={{ minWidth: 160 }}>
            {WINDOWS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!summary && loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : summary ? (
        <Box sx={{ opacity: loading ? 0.55 : 1, transition: "opacity 150ms" }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <StatCard label="Alerts" value={formatNumber(kpis.events)} hint={`${formatNumber(kpis.open)} still open`}
                icon={<NotificationsIcon />} color="warning" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="False-positive rate" value={formatPct(kpis.false_positive_pct)}
                hint={`${formatNumber(kpis.false_positives)} dismissed`} icon={<BlockIcon />} color="secondary" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Items reunited" value={formatNumber(kpis.reunions)}
                hint={`of ${formatNumber(kpis.lost_reports)} reports filed`} icon={<HandshakeIcon />} color="success" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Median time to reunite" value={formatDuration(kpis.median_reunite_s)}
                hint={kpis.fastest_reunite_s !== null ? `fastest ${formatDuration(kpis.fastest_reunite_s)}` : "no reunions in this window"}
                icon={<TimerIcon />} color="info" />
            </Grid>
          </Grid>

          {!hasEvents ? (
            <Card sx={{ p: 6, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">No alerts in the {windowLabel}</Typography>
              <Typography variant="body2" color="text.secondary">
                Nothing was left unattended long enough to raise an alert. Try a longer window or another camera.
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ChartCard title={hourly ? "Alerts per hour" : "Alerts per day"}
                  subtitle={`${formatNumber(kpis.events)} alerts, ${formatNumber(kpis.false_positives)} dismissed as false positives`}>
                  <Box sx={{ height: 280 }}>
                    <Line data={trend} options={baseOptions} />
                  </Box>
                </ChartCard>
              </Grid>
              <Grid item xs={12} lg={6}>
                <ChartCard title="By camera" subtitle="Valid alerts and false positives per camera">
                  <Box sx={{ height: byCamera.height }}>
                    <Bar data={byCamera.data} options={byCamera.options} />
                  </Box>
                </ChartCard>
              </Grid>
              <Grid item xs={12} lg={6}>
                <ChartCard title="By object type" subtitle="Alerts per detected class">
                  <Box sx={{ height: byClass.height }}>
                    <Bar data={byClass.data} options={byClass.options} />
                  </Box>
                </ChartCard>
              </Grid>
              <Grid item xs={12} lg={8}>
                <ChartCard title="When items are left" subtitle="Alerts by weekday and hour of day — hover a cell for the count">
                  <HourHeatmap cells={summary.heatmap} />
                </ChartCard>
              </Grid>
              <Grid item xs={12} lg={4}>
                <ChartCard title="Reunion funnel" subtitle={`Over the ${windowLabel}`}>
                  <Funnel stages={funnelStages} />
                </ChartCard>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent sx={{ pb: 0 }}>
                    <Typography variant="subtitle2" fontWeight="bold">Camera breakdown</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Unattended is how long an object sat still with no owner before the alert fired.
                    </Typography>
                  </CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Camera</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell align="right">Alerts</TableCell>
                          <TableCell align="right">Open</TableCell>
                          <TableCell align="right">False positives</TableCell>
                          <TableCell align="right">Avg unattended</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.by_camera.map((row) => (
                          <TableRow key={row.camera_id || "deleted"} hover>
                            <TableCell>{row.camera_name || "Deleted camera"}</TableCell>
                            <TableCell>{row.location || "—"}</TableCell>
                            <TableCell align="right">{formatNumber(row.events)}</TableCell>
                            <TableCell align="right">{formatNumber(row.open)}</TableCell>
                            <TableCell align="right">
                              {formatNumber(row.false_positives)} ({formatPct(row.false_positive_pct)})
                            </TableCell>
                            <TableCell align="right">{formatDuration(row.avg_unattended_s)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default LostFoundAnalytics;
